import type { BackdropStops, DocumentBackdrop } from '@/document/backdrop'

/**
 * Backgrounds a new world can start with (owner, 2026-10-15: "next to the size, we can have a
 * background selector ... take inspiration from backgrounds in frames, the gradients"). The home's
 * toggle steps through them. `plain` keeps each theme's own background colour; the others set the
 * document's background colour (`wall`, what thumbnails and anything colour-only use) and a gradient
 * drawn behind the scene (`SnapshotMeta.backdrop`). Stops after 033 Frames' rooms.
 */
export type HomeBackdrop = {
  id: string
  label: string
  /** The document's background colour; null: the theme's own. */
  wall: number | null
  backdrop: DocumentBackdrop | null
}

export const HOME_BACKDROPS: readonly HomeBackdrop[] = [
  { id: 'plain', label: 'Plain', wall: null, backdrop: null },
  {
    id: 'sky',
    label: 'Sky',
    wall: 0x9fd3f4,
    backdrop: { kind: 'vertical', stops: [[0, 0x5aa9e6], [0.55, 0x9fd3f4], [1, 0xe4f3fb]] },
  },
  {
    id: 'gallery',
    label: 'Gallery',
    wall: 0xebe6dc,
    backdrop: { kind: 'vertical', stops: [[0, 0xf7f4ee], [0.7, 0xebe6dc], [1, 0xddd6c9]] },
  },
  {
    id: 'sunset',
    label: 'Sunset',
    wall: 0xf9c99a,
    backdrop: { kind: 'vertical', stops: [[0, 0xf08a7a], [0.5, 0xf9c99a], [1, 0xfdebc8]] },
  },
  {
    id: 'rose',
    label: 'Rose',
    wall: 0xe39a8c,
    backdrop: { kind: 'vertical', stops: [[0, 0xf0b4a4], [0.6, 0xe39a8c], [1, 0xc77a6c]] },
  },
  {
    id: 'night',
    label: 'Night',
    wall: 0x231f3a,
    backdrop: { kind: 'glow', stops: [[0, 0x4a3f6a], [0.55, 0x2a2448], [1, 0x141026]], y: 0.45, radius: 0.9 },
  },
]

const hex = (color: number) => `#${color.toString(16).padStart(6, '0')}`
const cssStops = (stops: BackdropStops) => stops.map(([at, color]) => `${hex(color)} ${Math.round(at * 100)}%`).join(', ')

/** The backdrop as a CSS background (for the card and the toggle); null for plain. */
export function backdropCss(backdrop: DocumentBackdrop | null): string | null {
  if (!backdrop) return null
  if (backdrop.kind === 'vertical') return `linear-gradient(to bottom, ${cssStops(backdrop.stops)})`
  return `radial-gradient(ellipse ${Math.round(backdrop.radius * 100)}% ${Math.round(backdrop.radius * 100)}% at 50% ${Math.round(backdrop.y * 100)}%, ${cssStops(backdrop.stops)})`
}

const LAST_BACKDROP_KEY = 'huebo:home:last-backdrop'

export function readLastBackdrop(): HomeBackdrop {
  try {
    const id = localStorage.getItem(LAST_BACKDROP_KEY)
    return HOME_BACKDROPS.find((b) => b.id === id) ?? HOME_BACKDROPS[0]
  } catch {
    return HOME_BACKDROPS[0]
  }
}

export function writeLastBackdrop(backdrop: HomeBackdrop): void {
  try { localStorage.setItem(LAST_BACKDROP_KEY, backdrop.id) } catch { /* storage unavailable */ }
}
